import React, { useEffect, useState } from "react";
import { adminApi } from "./adminApi";

const StringArrayEditor = ({ value, onChange }) => {
  const arr = Array.isArray(value) ? value : [];
  const setAt = (i, v) => {
    const next = [...arr];
    next[i] = v;
    onChange(next);
  };
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const add = () => onChange([...arr, ""]);
  return (
    <div className="space-y-2">
      {arr.map((v, i) => (
        <div key={i} className="flex items-start gap-2">
          <textarea
            rows={2}
            value={v}
            onChange={(e) => setAt(i, e.target.value)}
            className="flex-1 bg-canvas border border-ink/15 focus:border-ink/40 outline-none px-3 py-2 text-sm rounded resize-none"
          />
          <button onClick={() => remove(i)} className="text-red-400 text-xs px-2">×</button>
        </div>
      ))}
      <button onClick={add} className="text-[10px] uppercase tracking-[0.3em] text-ink/70 hover:text-ink border border-ink/25 rounded-full px-3 py-1.5">
        + Add paragraph
      </button>
    </div>
  );
};

const SocialsEditor = ({ value, onChange }) => {
  const arr = Array.isArray(value) ? value : [];
  const setAt = (i, k, v) => {
    const next = arr.map((row, idx) => (idx === i ? { ...row, [k]: v } : row));
    onChange(next);
  };
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const add = () => onChange([...arr, { label: "", href: "" }]);
  return (
    <div className="space-y-2">
      {arr.map((row, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
          <input value={row.label || ""} onChange={(e) => setAt(i, "label", e.target.value)} placeholder="Label" className="bg-canvas border border-ink/15 outline-none px-3 py-2 text-sm rounded" />
          <input value={row.href || ""} onChange={(e) => setAt(i, "href", e.target.value)} placeholder="https://…" className="bg-canvas border border-ink/15 outline-none px-3 py-2 text-sm rounded" />
          <button onClick={() => remove(i)} className="text-red-400 text-xs px-2">×</button>
        </div>
      ))}
      <button onClick={add} className="text-[10px] uppercase tracking-[0.3em] text-ink/70 hover:text-ink border border-ink/25 rounded-full px-3 py-1.5">
        + Add social
      </button>
    </div>
  );
};

const SettingsEditor = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    adminApi.getSettings().then(setSettings);
  }, []);

  if (!settings) {
    return <p className="text-xs text-ink/50">Loading…</p>;
  }

  const setBrand = (k, v) => setSettings((s) => ({ ...s, brand: { ...s.brand, [k]: v } }));
  const setVideo = (k, v) => setSettings((s) => ({ ...s, videos: { ...s.videos, [k]: v } }));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await adminApi.updateSettings({
        brand: settings.brand,
        aboutCopy: settings.aboutCopy,
        newsletterCopy: settings.newsletterCopy,
        socials: settings.socials,
        marqueeLogos: settings.marqueeLogos,
        heroStills: settings.heroStills,
        videos: settings.videos,
      });
      setMsg("Saved.");
    } catch (err) {
      setMsg(err.message);
    } finally {
      setSaving(false);
    }
  };

  const brand = settings.brand || {};
  const videos = settings.videos || {};

  return (
    <div className="space-y-10">
      <div>
        <p className="text-[10px] tracking-[0.32em] uppercase text-ink/55">Collection</p>
        <h2 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight">Site Settings</h2>
      </div>

      <section className="bg-surface border border-ink/10 rounded-2xl p-6 space-y-4" data-testid="admin-settings-brand">
        <h3 className="text-[11px] uppercase tracking-[0.3em] text-ink/70">Brand</h3>
        {["name", "tagline", "email", "locations", "copyright"].map((k) => (
          <div key={k}>
            <label className="block text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-2">{k}</label>
            <input value={brand[k] || ""} onChange={(e) => setBrand(k, e.target.value)} className="w-full bg-canvas border border-ink/15 outline-none px-3 py-2 text-sm rounded" />
          </div>
        ))}
      </section>

      <section className="bg-surface border border-ink/10 rounded-2xl p-6 space-y-4">
        <h3 className="text-[11px] uppercase tracking-[0.3em] text-ink/70">About copy</h3>
        <StringArrayEditor value={settings.aboutCopy} onChange={(v) => setSettings((s) => ({ ...s, aboutCopy: v }))} />
      </section>

      <section className="bg-surface border border-ink/10 rounded-2xl p-6 space-y-4">
        <h3 className="text-[11px] uppercase tracking-[0.3em] text-ink/70">Socials</h3>
        <SocialsEditor value={settings.socials} onChange={(v) => setSettings((s) => ({ ...s, socials: v }))} />
      </section>

      <section className="bg-surface border border-ink/10 rounded-2xl p-6 space-y-4">
        <h3 className="text-[11px] uppercase tracking-[0.3em] text-ink/70">Videos</h3>
        {["hero", "workHeader", "capabilities", "about", "hybrid", "aiLed", "contentEngine"].map((k) => (
          <div key={k}>
            <label className="block text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-2">{k}</label>
            <input value={videos[k] || ""} onChange={(e) => setVideo(k, e.target.value)} className="w-full bg-canvas border border-ink/15 outline-none px-3 py-2 text-sm rounded" />
          </div>
        ))}
      </section>

      <section className="bg-surface border border-ink/10 rounded-2xl p-6 space-y-4">
        <h3 className="text-[11px] uppercase tracking-[0.3em] text-ink/70">Marquee logos (image URLs)</h3>
        <StringArrayEditor value={settings.marqueeLogos} onChange={(v) => setSettings((s) => ({ ...s, marqueeLogos: v }))} />
      </section>

      <section className="bg-surface border border-ink/10 rounded-2xl p-6 space-y-4">
        <h3 className="text-[11px] uppercase tracking-[0.3em] text-ink/70">Hero stills (image URLs)</h3>
        <StringArrayEditor value={settings.heroStills} onChange={(v) => setSettings((s) => ({ ...s, heroStills: v }))} />
      </section>

      <section className="bg-surface border border-ink/10 rounded-2xl p-6 space-y-4">
        <h3 className="text-[11px] uppercase tracking-[0.3em] text-ink/70">Newsletter copy</h3>
        <input value={settings.newsletterCopy || ""} onChange={(e) => setSettings((s) => ({ ...s, newsletterCopy: e.target.value }))} className="w-full bg-canvas border border-ink/15 outline-none px-3 py-2 text-sm rounded" />
      </section>

      <div className="flex items-center gap-4">
        <button onClick={save} disabled={saving} className="text-[11px] uppercase tracking-[0.3em] bg-ink text-canvas px-5 py-2.5 rounded-full disabled:opacity-50" data-testid="admin-settings-save">
          {saving ? "Saving…" : "Save settings"}
        </button>
        {msg && <p className="text-xs text-ink/70">{msg}</p>}
      </div>
    </div>
  );
};

export default SettingsEditor;
